import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Star, MapPin, Calendar, Check, MessageCircle, Settings, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import { api, useAuth, formatApiErrorDetail } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';

export default function ProfilePage() {
  const { id } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState({ name: '', bio: '', location: '' });
  const [saving, setSaving] = useState(false);

  const isOwnProfile = !id || id === currentUser?.id;
  const userId = isOwnProfile ? currentUser?.id : id;

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setProfile(data.user);
      setListings(data.listings);
      setEditData({
        name: data.user.name || '',
        bio: data.user.bio || '',
        location: data.user.location || ''
      });

      const reviewsRes = await api.get(`/users/${userId}/reviews`);
      setReviews(reviewsRes.data);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchProfile();
    }
  }, [userId, fetchProfile]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/profile', editData);
      toast.success('Profile updated!');
      setShowEditDialog(false);
      await refreshUser();
      fetchProfile();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="h-48 bg-surface-card animate-pulse mb-8" />
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="aspect-square bg-surface-card animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-surface-base">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <p className="text-zinc-400">Profile not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="p-8 bg-surface-card border border-border-subtle mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 bg-gold/10 flex items-center justify-center flex-shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-gold" />
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="font-clash text-2xl sm:text-3xl font-medium text-white">
                      {profile.name}
                    </h1>
                    {profile.is_verified && (
                      <span className="flex items-center gap-1 text-green-500 text-sm">
                        <Check className="w-4 h-4" /> Verified
                      </span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-gold fill-gold" />
                      {profile.rating?.toFixed(1) || '0.0'} ({profile.review_count || 0} reviews)
                    </span>
                    {profile.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {profile.location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Joined {new Date(profile.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {isOwnProfile ? (
                  <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                    <DialogTrigger asChild>
                      <button className="btn-secondary px-4 py-2 flex items-center gap-2" data-testid="edit-profile-btn">
                        <Settings className="w-4 h-4" /> Edit
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface-card border-border-subtle">
                      <DialogHeader>
                        <DialogTitle className="font-clash text-xl text-white">Edit Profile</DialogTitle>
                        <DialogDescription className="sr-only">Edit your profile information</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSaveProfile} className="space-y-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">Name</label>
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) => setEditData(d => ({ ...d, name: e.target.value }))}
                            className="input-dark w-full px-4 py-3"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">Bio</label>
                          <textarea
                            value={editData.bio}
                            onChange={(e) => setEditData(d => ({ ...d, bio: e.target.value }))}
                            className="input-dark w-full px-4 py-3 h-24 resize-none"
                            placeholder="Tell us about yourself..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-zinc-300 mb-2">Location</label>
                          <input
                            type="text"
                            value={editData.location}
                            onChange={(e) => setEditData(d => ({ ...d, location: e.target.value }))}
                            className="input-dark w-full px-4 py-3"
                            placeholder="e.g., Los Angeles, CA"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={saving}
                          className="btn-gold w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Changes'}
                        </button>
                      </form>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Link
                    to={`/messages?receiver=${userId}`}
                    state={{ receiverId: userId }}
                    className="btn-secondary px-4 py-2 flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" /> Message
                  </Link>
                )}
              </div>

              {profile.bio && (
                <p className="mt-4 text-zinc-400">{profile.bio}</p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-border-subtle">
          <button
            onClick={() => setActiveTab('listings')}
            className={`px-4 py-3 -mb-px text-sm font-medium transition-colors ${
              activeTab === 'listings' 
                ? 'text-gold border-b-2 border-gold' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Listings ({listings.length})
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-3 -mb-px text-sm font-medium transition-colors ${
              activeTab === 'reviews' 
                ? 'text-gold border-b-2 border-gold' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            Reviews ({reviews.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'listings' ? (
          listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {listings.map((listing, idx) => (
                <ListingCard key={listing.id} listing={listing} index={idx} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-zinc-400">No listings yet</p>
            </div>
          )
        ) : (
          reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map(review => (
                <div key={review.id} className="p-6 bg-surface-card border border-border-subtle">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'text-gold fill-gold' : 'text-zinc-600'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-zinc-400">by {review.reviewer_name}</span>
                      </div>
                      <p className="mt-2 text-zinc-300">{review.comment}</p>
                    </div>
                    <span className="text-xs text-zinc-500">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-zinc-400">No reviews yet</p>
            </div>
          )
        )}
      </main>

      <Footer />
    </div>
  );
}
